import React, { useState, useContext, useEffect, useRef, useMemo } from "react";
import { toast } from "react-toastify";
import { showSuccess, showError } from "../../../../utils/toast";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import IconButton from "@mui/material/IconButton";
import { RiCloseCircleFill } from "react-icons/ri";
import PropTypes from "prop-types";
import InputUI from "../../../material-ui/InputUI";
import ButtonUI from "../../../material-ui/ButtonUI";
import { AuthContext } from "../../../../context/Auth/AuthContext";
import addDocument from "../../../../api/addDocument";
import addAdditionalDocument from "../../../../api/addAdditionalDocument";
import UploadIcon from "@mui/icons-material/Upload";
import "../../../../styles/AddDocument.css";
import baseUrl from "../../../../config/baseUrl";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { BiLinkAlt } from "react-icons/bi";

const MAX_FILE_SIZE = 300 * 1024 * 1024; // 300MB
const COMPRESSION_THRESHOLD = 5 * 1024 * 1024; // 5MB

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

const BootstrapDialogTitle = (props) => {
  const { children, onClose, ...other } = props;

  return (
    <DialogTitle sx={{ m: 0, p: 2 }} {...other}>
      {children}
      {onClose ? (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <RiCloseCircleFill />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
};

BootstrapDialogTitle.propTypes = {
  children: PropTypes.node,
  onClose: PropTypes.func.isRequired,
};

const SUPPORTED_FORMATS = [
  "image/jpg",
  "image/jpeg",
  "image/gif",
  "image/png",
  "image/bmp",
  "image/webp",
  "image/heic",
  "image/heif",
  "image/tiff",
  "image/x-icon",
  "image/pjpeg",
  "text/plain",
  "text/csv",
  "text/html",
  "application/pdf",
  "application/rtf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/zip",
  "application/x-zip-compressed",
  "application/vnd.rar",
  "application/x-rar-compressed",
  "audio/mpeg",
  "audio/mp4",
  "audio/aac",
  "audio/x-aac",
  "audio/amr",
  "audio/opus",
  "audio/x-m4a",
  "audio/wav",
  "audio/x-wav",
  "audio/wave",
  "audio/aiff",
  "audio/x-aiff",
  "audio/flac",
  "audio/x-flac",
  "audio/alac",
  "audio/x-alac",
  "audio/ogg",
  "audio/vorbis",
  "application/ogg",
  "audio/x-ms-wma",
  "audio/wma",
  "audio/midi",
  "audio/x-midi",
  "audio/mp1",
  "audio/mp2",
  "audio/mpa",
  "video/mp4",
  "video/quicktime",
  "video/x-ms-wmv",
  "video/3gpp",
  "video/mpeg",
  "audio/3gpp",
  "application/onenote",
  "application/x-onenote",
  "application/ms-onenote",
  "message/rfc822",
  "text/calendar",
  "application/x-mimearchive",
  "application/vnd.ms-outlook",
  "application/x-msg",
  "application/vnd.apple.pages",
  "application/x-iwork-pages-sffpages",
  "application/internet-shortcut",
  "application/x-mswinurl",
  "text/vcard",
  "text/x-vcard",
  "application/vcard",
  "application/octet-stream",
];

const ADDITIONAL_ACCEPT_EXTS = [
  ".one",
  ".eml",
  ".ics",
  ".jfif",
  ".mhtml",
  ".pages",
  ".url",
  ".vcf",
  ".3gp",
  ".msg",
  ".wmv",
  ".opus",
  ".amr",
  ".crdownload",
  ".bmp",
  ".htm",
  ".html",
  ".wav",
  ".aiff",
  ".aif",
  ".flac",
  ".alac",
  ".ogg",
  ".oga",
  ".wma",
  ".midi",
  ".mid",
  ".mp1",
  ".mp2",
  ".mpa",
  ".mpeg",
  ".mpg",
];

const ALLOWED_EXT_REGEX =
  /\.(jpg|jpeg|jfif|gif|png|bmp|pdf|xlsx|xls|zip|doc|docx|rar|ppt|pptx|txt|rtf|mp4|mov|wmv|mp3|aac|amr|opus|tiff|tif|webp|heic|heif|ico|csv|html|htm|bin|one|m4a|eml|ics|mhtml|pages|url|vcf|3gp|msg|crdownload|wav|aiff|aif|flac|alac|ogg|oga|wma|midi|mid|mp1|mp2|mpa|mpeg|mpg)$/i;

const getFileExtension = (name = "") =>
  name.split(".").pop()?.toLowerCase() || "";

const normalizeExt = (ext = "") => {
  const e = (ext || "").toLowerCase();
  if (e === "jpg") return "jpeg";
  if (e === "jfif") return "jpeg";
  if (e === "htm") return "html";
  if (e === "tif") return "tiff";
  if (e === "doc") return "docx";
  if (e === "aif") return "aiff";
  if (e === "oga") return "ogg";
  if (e === "mid") return "midi";
  if (e === "mpg") return "mpeg";
  return e;
};

const isLinkableExt = (ext = "") =>
  ["pdf", "jpg", "jpeg", "png", "doc", "docx"].includes(ext.toLowerCase());

const addDocumentFormSchema = yup.object().shape({
  documentName: yup.string().required("Document Name is required field"),
  SubCategory: yup.string().required("Sub Category is required field"),
  addRequestName: yup.string(),
  fileUpload: yup
    .mixed()
    .test("required", "Please select a file", (value) => {
      return value && value.length > 0;
    })
    .test("type", "Unsupported file type", (value) => {
      const f = value?.[0];
      if (!f) return false;
      const byMime = !!f.type && SUPPORTED_FORMATS.includes(f.type);
      const byExt = ALLOWED_EXT_REGEX.test(f.name || "");
      return byMime || byExt;
    })
    .test(
      "fileSize",
      "Please upload file having size less than 300MB", // ← updated
      (value) => {
        return value && value[0]?.size <= MAX_FILE_SIZE; // ← updated
      },
    ),
});

const UploadDocumentsPopup = ({
  clientID,
  documentUpdatedToTrue,
  setDocumentUpdatedToTrue,
  subCategoryList,
}) => {
  const [loading, setLoading] = useState(false);
  const [requestOptions, setRequestOptions] = useState([]);
  const { token, user_id, is_admin } = useContext(AuthContext);
  const [open, setOpen] = useState(false);
  const [linkRequest, setLinkRequest] = useState(false);
  const [isFileValidForLinking, setIsFileValidForLinking] = useState(false);
  const [hasLargeFile, setHasLargeFile] = useState(false); // ← add

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
    clearErrors,
    reset,
  } = useForm({
    resolver: yupResolver(addDocumentFormSchema),
  });

  const fileUploadValue = watch("fileUpload");
  const selectedFile = fileUploadValue ? fileUploadValue[0] : null;

  const fileExtension = useMemo(
    () =>
      selectedFile ? normalizeExt(getFileExtension(selectedFile.name)) : "",
    [selectedFile],
  );

  useEffect(() => {
    if (selectedFile) {
      // ← size check here instead of in onChange
      if (selectedFile.size > MAX_FILE_SIZE) {
        showError(`${selectedFile.name} exceeds the 300MB limit`);
        setValue("fileUpload", null);
        setHasLargeFile(false);
        setIsFileValidForLinking(false);
        return;
      }
      const valid = isLinkableExt(fileExtension);
      setIsFileValidForLinking(valid);
      setHasLargeFile(selectedFile.size > COMPRESSION_THRESHOLD);
    } else {
      setIsFileValidForLinking(false);
      setLinkRequest(false);
      setValue("addRequestName", "");
      setHasLargeFile(false);
    }
  }, [fileUploadValue, setValue, selectedFile, fileExtension, linkRequest]);
  const filteredRequestOptions = useMemo(() => {
    if (!Array.isArray(requestOptions)) return [];

    const ADDITIONAL_ID = "Additional Document";

    return requestOptions.filter((o) => {
      if (o?.document_request_id === ADDITIONAL_ID) return true;

      const isPresent = Number(o?.is_present) === 1;
      if (!isPresent) return true;

      if (!isFileValidForLinking) return false;

      if (!fileExtension) return false;
      return normalizeExt(o?.document_type || "") === fileExtension;
    });
  }, [requestOptions, isFileValidForLinking, fileExtension]);

  const filterOptions = createFilterOptions({
    matchFrom: "any",
    ignoreCase: true,
    stringify: (option) => option.fields_name,
  });

  const handleResetForm = () => {
    clearErrors();
    reset();
    setLinkRequest(false);
    setIsFileValidForLinking(false);
    setHasLargeFile(false); // ← add
  };

  const onAddDocumentFormSubmit = async (addDocumentFormData) => {
    setLoading(true);

    if (
      addDocumentFormData.addRequestName &&
      addDocumentFormData.addRequestName !== "Additional Document"
    ) {
      const addDocumentFormResponse = await addDocument({
        token: token,
        user_id: user_id,
        contact_id: clientID,
        document_name: addDocumentFormData.documentName,
        document_desc: addDocumentFormData.documentDescription,
        document_request_id: addDocumentFormData.addRequestName,
        category_id: "0c73a993-4dce-4430-b5a9-9d4ea57739e8",
        document_sub_category: addDocumentFormData.SubCategory,
        favourite: "0",
        document_url: addDocumentFormData.fileUpload[0],
      });

      if (addDocumentFormResponse?.statusCode === 200) {
        setLoading(false);
        setOpen(false);
        setDocumentUpdatedToTrue((count) => count + 1);
        if (addDocumentFormResponse?.combinedDoc) {
          showSuccess("Document is merged for the link request successfully");
        } else {
          showSuccess("Document is added successfully");
        }
      } else {
        setLoading(false);
        setOpen(false);
        toast.error(
          addDocumentFormResponse?.message || "Error while adding document",
          {
            position: "top-center",
            autoClose: 5000,
            theme: "dark",
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
          },
        );
      }
    } else {
      const addAdditionalDocumentFormResponse = await addAdditionalDocument({
        token: token,
        user_id: user_id,
        contact_id: clientID,
        document_name: addDocumentFormData.documentName,
        document_desc: addDocumentFormData.documentDescription,
        category_id: "0c73a993-4dce-4430-b5a9-9d4ea57739e8",
        document_sub_category: addDocumentFormData.SubCategory,
        content_type: "pdf",
        document_url: addDocumentFormData.fileUpload[0],
      });

      if (addAdditionalDocumentFormResponse?.statusCode === 200) {
        setLoading(false);
        setOpen(false);
        setDocumentUpdatedToTrue((count) => count + 1);
        showSuccess("Document is added successfully");
      } else {
        setLoading(false);
        setOpen(false);
        showError("Error while adding document");
      }
    }
  };

  const fetchRequestList = async () => {
    const url = `${baseUrl}/api/get-client-document-requests-for-linking?userId=1&isAdmin=1&contact_id=${clientID}`;
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (data?.code === 200) {
        const additionalItem = {
          document_request_id: "Additional Document",
          document_req_name: "Additional Document",
        };
        const updatedData = [
          additionalItem,
          ...data?.data?.document_requests_for_linking,
        ];
        setRequestOptions(updatedData);
      } else {
        setRequestOptions([
          {
            document_request_id: "Additional Document",
            document_req_name: "Additional Document",
          },
        ]);
      }
    } catch (error) {
      return error;
    }
  };

  const handleRequestListLink = () => {
    if (clientID && filteredRequestOptions?.length > 0) {
      setLinkRequest(true);
    }
  };

  const handleClickOpen = () => {
    setLinkRequest(false);
    handleResetForm();
    setOpen(true);
    fetchRequestList();
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <div className="dialog-modal">
      <ButtonUI
        formButton={false}
        variant="contained"
        buttonText="Upload Documents"
        style={{
          width: "169px",
          height: "35px",
          color: "#fff",
          marginRight: "10px",
          backgroundColor: "#008000",
          boxShadow: "none",
          fontWeight: "200",
          borderRadius: "4px",
          letterSpacing: "0.07rem",
        }}
        onClickFunc={handleClickOpen}
      />
      <BootstrapDialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        className="custom-edit-modal"
        open={open}
      >
        <BootstrapDialogTitle
          id="customized-dialog-title"
          className="edit-doc-modal"
          onClose={handleClose}
        >
          <div className="popup-header-svg-icon">
            <UploadIcon />
          </div>
          <div className="popup-header-title">Upload Document</div>
        </BootstrapDialogTitle>

        <DialogContent dividers>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <InputUI
                fullWidth
                name="documentName"
                label="Document Name"
                type="text"
                variant="outlined"
                cssStyles={{ margin: "10px 0px" }}
                register={register}
              />
              <span className="add-doc-error-message">
                {errors["documentName"]?.message}
              </span>
            </div>
            <div>
              <InputUI
                fullWidth
                name="documentDescription"
                label="Document Description"
                type="text"
                variant="outlined"
                cssStyles={{ margin: "10px 0px" }}
                register={register}
              />
              <span className="add-doc-error-message">
                {errors["documentDescription"]?.message}
              </span>
            </div>
            <div>
              <InputUI
                fullWidth
                name="Category"
                label="Category"
                type="text"
                variant="outlined"
                disabled
                value="Manual Process"
                register={register}
              />
            </div>
            <div>
              <FormControl fullWidth>
                <Controller
                  control={control}
                  name="SubCategory"
                  render={({ field: { onChange, value } }) => (
                    <Autocomplete
                      options={subCategoryList}
                      getOptionLabel={(option) => option.fields_name || ""}
                      filterOptions={filterOptions}
                      onChange={(e, value) => {
                        onChange(value ? value.field_setting_id : "");
                      }}
                      value={
                        subCategoryList?.find(
                          (option) => option.field_setting_id === value,
                        ) || null
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Sub Category"
                          variant="outlined"
                        />
                      )}
                    />
                  )}
                />
              </FormControl>
              <span className="add-doc-error-message">
                {errors["SubCategory"]?.message}
              </span>
            </div>

            {/* ── File input with size hint + large file warning ── */}
            <div>
              <InputUI
                fullWidth
                name="fileUpload"
                type="file"
                variant="outlined"
                register={register}
              />
              <small className="text-sm text-gray-500 ml-1">
                Maximum upload size: 300 MB
              </small>
              {hasLargeFile && (
                <div className="w-full mt-2 p-3 bg-yellow-100 text-yellow-800 rounded-md text-center font-medium text-sm">
                  ⚠️ Large file detected (greater than 5MB). Upload may take
                  longer due to compression. Please wait...
                </div>
              )}
              <span className="add-doc-error-message">
                {errors["fileUpload"]?.message}
              </span>
            </div>
            <div>
              {selectedFile &&
              !linkRequest &&
              filteredRequestOptions?.length > 0 ? (
                <span
                  className="request-list-link"
                  onClick={handleRequestListLink}
                >
                  Link to Request List
                </span>
              ) : null}

              {clientID && linkRequest ? (
                <FormControl fullWidth>
                  <Controller
                    control={control}
                    name="addRequestName"
                    render={({ field: { onChange, value } }) => {
                      const selectedOption = filteredRequestOptions?.find(
                        (option) => option.document_request_id === value,
                      );

                      return (
                        <Autocomplete
                          options={filteredRequestOptions}
                          filterOptions={createFilterOptions({
                            matchFrom: "any",
                            ignoreCase: true,
                            stringify: (option) => option.document_req_name,
                          })}
                          getOptionLabel={(option) =>
                            option.document_req_name || ""
                          }
                          onChange={(e, val) =>
                            onChange(val ? val.document_request_id : "")
                          }
                          value={selectedOption || null}
                          renderOption={(props, option) => (
                            <li
                              {...props}
                              key={option.document_request_id}
                              style={{ display: "flex", alignItems: "center" }}
                            >
                              {option.is_present === 1 &&
                                isFileValidForLinking &&
                                normalizeExt(option.document_type || "") ===
                                  fileExtension && (
                                  <BiLinkAlt
                                    style={{
                                      fontSize: 20,
                                      marginRight: "8px",
                                      color: "#1976d2",
                                    }}
                                  />
                                )}
                              <span style={{ flex: 1 }}>
                                {option.document_req_name}
                              </span>
                            </li>
                          )}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Request List"
                              variant="outlined"
                            />
                          )}
                        />
                      );
                    }}
                  />
                </FormControl>
              ) : null}
            </div>
          </div>
        </DialogContent>

        <DialogActions sx={{ paddingBottom: "15px !important" }}>
          <div className="popup_submit_btn">
            {/* ← spinner button replacing ButtonUI */}
            <button
              type="button"
              disabled={loading}
              onClick={handleSubmit(onAddDocumentFormSubmit)}
              style={{
                width: "200px", // ← was too small due to dialog padding
                minWidth: "200px", // ← add this to prevent shrinking
                height: "40px", // ← slightly taller
                backgroundColor: "#1a6b7c",
                border: "1px solid #1a6b7c",
                borderRadius: "4px",
                marginTop: "20px",
                color: "white",
                fontWeight: "500",
                letterSpacing: "0.07rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px",
                opacity: loading ? 0.7 : 1,
                cursor: loading ? "not-allowed" : "pointer",
                fontSize: "14px", // ← ensure text isn't too small
              }}
            >
              {loading ? (
                <>
                  <svg
                    className="animate-spin h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  Uploading...
                </>
              ) : (
                "Upload Document"
              )}
            </button>
          </div>
        </DialogActions>
      </BootstrapDialog>
    </div>
  );
};

export default UploadDocumentsPopup;
